import React, { useState, useEffect } from "react";
import { Modal, Form, InputNumber, Select, Radio, Button, DatePicker, Checkbox, Row, Col, Segmented, Divider, Tag } from "antd";
import { getGameTypes } from "../../api/services/gameTypeService";
import { getPlayers } from "../../api/services/playerService";
import { getTeams } from "../../api/services/teamService";

const AddMatchModal = ({ visible, onClose, onCreateMatch }) => {
  const [form] = Form.useForm();
  const [gameTypes, setGameTypes] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [raceType, setRaceType] = useState("race_to"); // Default: Race-to
  const [isOpenEnd, setIsOpenEnd] = useState(false);
  const [isPractice, setIsPractice] = useState(false);
  const [isTeamMatch, setIsTeamMatch] = useState(false);
  const [team1Type, setTeam1Type] = useState("permanent"); // permanent oder adhoc
  const [team2Type, setTeam2Type] = useState("permanent");

  useEffect(() => {
    const fetchData = async () => {
      const fetchedGameTypes = await getGameTypes();
      const fetchedPlayers = await getPlayers();
      setGameTypes(fetchedGameTypes);
      setPlayers(fetchedPlayers);
      try {
        const fetchedTeams = await getTeams();
        setTeams(fetchedTeams);
      } catch (e) {
        console.log("Teams nicht geladen:", e);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // Team-Match Daten aufbereiten
      const matchData = {
        ...values,
        is_team_match: isTeamMatch,
      };

      if (isTeamMatch) {
        // Team 1
        if (team1Type === "permanent") {
          matchData.team1 = values.team1;
        } else {
          matchData.team1_player_ids = values.team1_players;
          matchData.team1_name = values.team1_name || null;
        }
        // Team 2
        if (team2Type === "permanent") {
          matchData.team2 = values.team2;
        } else {
          matchData.team2_player_ids = values.team2_players;
          matchData.team2_name = values.team2_name || null;
        }
        // player1/player2 entfernen bei Team-Match
        delete matchData.player1;
        delete matchData.player2;
      }

      onCreateMatch(matchData);
      form.resetFields();
      setIsTeamMatch(false);
      setTeam1Type("permanent");
      setTeam2Type("permanent");
      onClose();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleMatchTypeChange = (value) => {
    const isTeam = value === "team";
    setIsTeamMatch(isTeam);
    if (isTeam) {
      setIsPractice(false);
      form.setFieldsValue({ is_practice_match: false });
    }
  };

  return (
    <Modal
      title="Match anlegen"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Abbrechen
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Match erstellen
        </Button>,
      ]}
      style={{ top: 20 }}
      styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
    >
      <Form form={form} layout="vertical" initialValues={{
    race_to: 1,
    best_of: 1,
    is_open_end: false,
    is_practice_match: false,
  }}>
        {/* Match-Typ Auswahl */}
        <Form.Item label="Match-Typ">
          <Segmented
            options={[
              { label: 'Einzel', value: 'single' },
              { label: 'Team', value: 'team' },
            ]}
            value={isTeamMatch ? 'team' : 'single'}
            onChange={handleMatchTypeChange}
            block
          />
        </Form.Item>

        {/* EINZEL-MATCH */}
        {!isTeamMatch && (
          <Row gutter={[8, 0]}>
            <Col xs={12} sm={12} md={24}>
              <Form.Item
                name="player1"
                label="Spieler 1"
                rules={[{ required: !isTeamMatch, message: "Bitte Spieler 1 auswählen!" }]}
              >
                <Select
                  placeholder="Spieler auswählen"
                  onChange={(value) => {
                    if (isPractice) {
                      form.setFieldsValue({ player2: value });
                    }
                  }}
                >
                  {players.map((player) => (
                    <Select.Option key={player.id} value={player.id}>
                      {player.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={12} sm={12} md={24}>
              <Form.Item
                name="player2"
                label="Spieler 2"
                rules={[{ required: !isTeamMatch && !isPractice, message: "Bitte Spieler 2 auswählen!" }]}
              >
                <Select placeholder="Spieler auswählen" disabled={isPractice}>
                  {players.map((player) => (
                    <Select.Option key={player.id} value={player.id}>
                      {player.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* TEAM-MATCH */}
        {isTeamMatch && (
          <>
            {/* Team 1 */}
            <Divider orientation="left" style={{ margin: '8px 0' }}>Team 1</Divider>
            <Segmented
              options={[
                { label: 'Bestehendes Team', value: 'permanent' },
                { label: 'Ad-hoc Team', value: 'adhoc' },
              ]}
              value={team1Type}
              onChange={setTeam1Type}
              block
              size="small"
              style={{ marginBottom: 8 }}
            />
            {team1Type === "permanent" ? (
              <Form.Item
                name="team1"
                rules={[{ required: isTeamMatch && team1Type === "permanent", message: "Bitte Team 1 auswählen!" }]}
              >
                <Select placeholder="Team auswählen">
                  {teams.map((team) => (
                    <Select.Option key={team.id} value={team.id}>
                      {team.name} {team.player_names && <Tag>{team.player_names.join(', ')}</Tag>}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <>
                <Form.Item name="team1_name" style={{ marginBottom: 8 }}>
                  <Select
                    placeholder="Team-Name (optional)"
                    mode="tags"
                    maxTagCount={1}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  name="team1_players"
                  rules={[{ required: isTeamMatch && team1Type === "adhoc", message: "Bitte Spieler auswählen!" }]}
                >
                  <Select mode="multiple" placeholder="Spieler auswählen">
                    {players.map((player) => (
                      <Select.Option key={player.id} value={player.id}>
                        {player.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            {/* Team 2 */}
            <Divider orientation="left" style={{ margin: '8px 0' }}>Team 2</Divider>
            <Segmented
              options={[
                { label: 'Bestehendes Team', value: 'permanent' },
                { label: 'Ad-hoc Team', value: 'adhoc' },
              ]}
              value={team2Type}
              onChange={setTeam2Type}
              block
              size="small"
              style={{ marginBottom: 8 }}
            />
            {team2Type === "permanent" ? (
              <Form.Item
                name="team2"
                rules={[{ required: isTeamMatch && team2Type === "permanent", message: "Bitte Team 2 auswählen!" }]}
              >
                <Select placeholder="Team auswählen">
                  {teams.map((team) => (
                    <Select.Option key={team.id} value={team.id}>
                      {team.name} {team.player_names && <Tag>{team.player_names.join(', ')}</Tag>}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <>
                <Form.Item name="team2_name" style={{ marginBottom: 8 }}>
                  <Select
                    placeholder="Team-Name (optional)"
                    mode="tags"
                    maxTagCount={1}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  name="team2_players"
                  rules={[{ required: isTeamMatch && team2Type === "adhoc", message: "Bitte Spieler auswählen!" }]}
                >
                  <Select mode="multiple" placeholder="Spieler auswählen">
                    {players.map((player) => (
                      <Select.Option key={player.id} value={player.id}>
                        {player.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}
          </>
        )}

        {/* Spielmodus */}
        <Form.Item
          name="game_type_id"
          label="Spielmodus"
          rules={[{ required: true, message: "Bitte Spielmodus auswählen!" }]}
        >
          <Select placeholder="Spielmodus auswählen">
            {gameTypes.map((type) => (
              <Select.Option key={type.id} value={type.id}>
                {type.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Übungsspiel und Open End Checkboxen nebeneinander auf Mobile */}
        <Row gutter={[8, 0]}>
          {!isTeamMatch && (
            <Col xs={24} sm={12} md={24}>
              {/* Übungsspiel Checkbox - nur für Einzel-Matches */}
              <Form.Item name="is_practice_match" valuePropName="checked">
                <Checkbox
                  onChange={(e) => {
                    setIsPractice(e.target.checked);
                    if (e.target.checked) {
                      const player1 = form.getFieldValue("player1");
                      form.setFieldsValue({ player2: player1 });
                    }
                  }}
                >
                  Übungsspiel (gegen sich selbst)
                </Checkbox>
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12} md={24}>
            {/* Open End Checkbox */}
            <Form.Item name="is_open_end" valuePropName="checked">
              <Checkbox
                onChange={(e) => {
                  setIsOpenEnd(e.target.checked);
                  if (e.target.checked) {
                    form.setFieldsValue({ race_to: null, best_of: null });
                  }
                }}
              >
                Open End (unbegrenzte Frames)
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {/* Race-to / Best-of nur anzeigen wenn nicht Open End */}
        {!isOpenEnd && (
          <>
            <Form.Item label="Modus">
              <Radio.Group
                onChange={(e) => setRaceType(e.target.value)}
                value={raceType}
              >
                <Radio value="race_to">Race-to</Radio>
                <Radio value="best_of">Best-of</Radio>
              </Radio.Group>
            </Form.Item>

            {/* Eingabefeld für Race-to oder Best-of */}
            <Form.Item
              name={raceType}
              label={raceType === "race_to" ? "Race-to" : "Best-of"}
              rules={[{ required: !isOpenEnd, message: "Bitte eine Zahl eingeben!" }]}
            >
              <InputNumber min={1} step={2} placeholder="Zahl eingeben" />
            </Form.Item>
          </>
        )}

                {/* Datum und Uhrzeit */}
        <Form.Item
          name="date"
          label="Datum und Uhrzeit"
          rules={[{ required: true, message: "Bitte Datum und Uhrzeit auswählen!" }]}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder="Datum und Uhrzeit auswählen"
            style={{ width: '100%' }}
            size="small"
            getPopupContainer={(trigger) => trigger.parentElement}
            panelRender={(panelNode) => (
              <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                {panelNode}
              </div>
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddMatchModal;